import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { parseFlow } from '../types';
import type { BotAction, BotFlow, FlowDefinition, FlowNode, FlowReport } from '../types';
import type { RehearsalTurn, RehearseRequest } from '../api';
import { FlowMap } from './FlowMap';
import { NodeEditor } from './NodeEditor';
import { FlowEmulator } from './FlowEmulator';

type View = 'steps' | 'map' | 'json';

/**
 * The four calls this editor makes.
 *
 * Taken as a prop rather than imported, because the same editor is the client's bot
 * builder and PSM's. The two reach different routes — a client's are scoped to their
 * own tenant by the server, PSM's address any tenant by id — and the difference is
 * entirely in the URL. Passing it in keeps that difference in one small adapter per
 * app instead of a second copy of this file, which is where the two would quietly
 * stop being the same builder.
 */
export interface FlowStudioApi {
  validate: (definition: string) => Promise<FlowReport>;
  save: (id: number, definition: string) => Promise<unknown>;
  publish: (id: number) => Promise<unknown>;
  rehearse: (body: RehearseRequest) => Promise<RehearsalTurn>;
}

/**
 * One flow: a workspace, not a form.
 *
 * Two panes. The left is the flow — as steps, as a map, or as the definition itself —
 * and the right is a conversation with it. That split is the whole design: a bot is
 * read by editing one thing and immediately saying hello to it, and a builder that
 * makes you publish before you can see what you built is a builder people stop using.
 *
 * All three views edit the same definition and all three are checked by the same
 * server call that decides whether it can be published, so there is no view in which
 * a flow can be made invalid quietly.
 */
export function FlowEditor({ flow, actions, api, onClose }: {
  flow: BotFlow;
  actions: BotAction[];
  api: FlowStudioApi;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [view, setView] = useState<View>('steps');
  const [text, setText] = useState(flow.definition);
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [report, setReport] = useState<FlowReport | null>(null);
  const [showTest, setShowTest] = useState(true);

  const definition = useMemo(() => parseFlow(text), [text]);
  const nodes = definition.nodes ?? {};
  const dirty = text !== flow.definition;

  // Whether the text is even JSON, separately from whether the flow is valid. The two
  // are different failures and reporting them the same way is why a stray comma used
  // to read as "your flow has no steps".
  const parses = useMemo(() => {
    try { JSON.parse(text); return true; } catch { return false; }
  }, [text]);

  // Held in a ref so the debounce below depends on the text and nothing else. A caller
  // that rebuilds its api object each render — the obvious way to write one — would
  // otherwise restart the timer on every keystroke's re-render.
  const latest = useRef(api);
  latest.current = api;

  useEffect(() => {
    if (!parses) { setReport(null); return; }
    const id = setTimeout(() => {
      latest.current.validate(text).then(setReport).catch(() => setReport(null));
    }, 400);
    return () => clearTimeout(id);
  }, [text, parses]);

  const write = (next: FlowDefinition) => setText(JSON.stringify(next, null, 2));

  const patchNode = (id: string, node: FlowNode) => write({ ...definition, nodes: { ...nodes, [id]: node } });

  const renameNode = (from: string, to: string) => {
    if (!to || to === from) return;

    // Every reference is rewritten with the name. A rename that left the old id in
    // half a dozen transitions would produce exactly the dangling targets this editor
    // exists to make impossible.
    const renamed: Record<string, FlowNode> = {};
    for (const [id, node] of Object.entries(nodes)) {
      const copy: FlowNode = { ...node };
      for (const key of ['next', 'default', 'empty_next']) {
        if (copy[key] === from) copy[key] = to;
      }
      for (const key of ['transitions', 'cases']) {
        const map = copy[key] as Record<string, string> | undefined;
        if (map) copy[key] = Object.fromEntries(Object.entries(map).map(([k, v]) => [k, v === from ? to : v]));
      }
      if (Array.isArray(copy.extra_rows)) {
        copy.extra_rows = (copy.extra_rows as Record<string, unknown>[])
          .map((e) => (e?.target === from ? { ...e, target: to } : e));
      }
      renamed[id === from ? to : id] = copy;
    }

    write({ ...definition, nodes: renamed, start: definition.start === from ? to : definition.start });
    setSelected(to);
  };

  const removeNode = (id: string) => {
    const rest = { ...nodes };
    delete rest[id];
    write({ ...definition, nodes: rest });
    if (selected === id) setSelected(null);
  };

  const addNode = () => {
    let name = 'step';
    let n = 1;
    while (nodes[name]) name = `step_${++n}`;
    write({ ...definition, nodes: { ...nodes, [name]: { type: 'message', body: '', next: null } } });
    setSelected(name);
    setView('steps');
  };

  const save = useMutation({
    mutationFn: () => latest.current.save(flow.id, text),
    onSuccess: () => {
      setError(null);
      setNote('Saved.');
      void queryClient.invalidateQueries({ queryKey: ['flow', flow.id] });
      void queryClient.invalidateQueries({ queryKey: ['bot-flow', flow.id] });
      void queryClient.invalidateQueries({ queryKey: ['flows'] });
      void queryClient.invalidateQueries({ queryKey: ['bot-flows'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not save.'),
  });

  const publish = useMutation({
    mutationFn: () => latest.current.publish(flow.id),
    onSuccess: () => { setError(null); onClose(); },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not publish.'),
  });

  const problems = (report?.errors.length ?? 0) + (report?.warnings.length ?? 0);

  return (
    <div className="flex flex-col gap-4">
      {/* ------------------------------------------------------------- header -- */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg border border-(--color-line) px-3.5 py-2 text-body"
        >
          ← All bots
        </button>

        <div className="min-w-0">
          <h1 className="truncate text-title font-semibold tracking-tight">{flow.name}</h1>
          <p className="text-small text-(--color-muted)">
            Version {flow.version}
            {flow.isPublished ? ' · published, read only' : ' · draft'}
            {dirty ? ' · unsaved changes' : ''}
          </p>
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setShowTest((v) => !v)}
            className="rounded-lg border border-(--color-line) px-3.5 py-2 text-body xl:hidden"
          >
            {showTest ? 'Hide test' : 'Test it'}
          </button>
          <button
            type="button"
            disabled={!dirty || save.isPending || flow.isPublished || !parses}
            onClick={() => save.mutate()}
            title={!parses ? 'The definition is not valid JSON' : undefined}
            className="rounded-lg border border-(--color-line) px-4 py-2 text-body disabled:opacity-40"
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            disabled={dirty || publish.isPending || flow.isPublished || report?.ok === false}
            title={dirty ? 'Save first' : report?.ok === false ? 'Fix the errors first' : undefined}
            onClick={() => publish.mutate()}
            className="rounded-lg bg-(--color-brand) px-4 py-2 text-body font-medium text-white disabled:opacity-40"
          >
            {publish.isPending ? 'Publishing…' : 'Publish this version'}
          </button>
        </div>
      </div>

      {error && (
        <p role="alert" className="rounded-lg border border-(--color-bad) px-4 py-3 text-body text-(--color-bad)">
          {error}
        </p>
      )}
      {note && !error && (
        <p className="rounded-lg border border-(--color-good) px-4 py-3 text-body text-(--color-good)">{note}</p>
      )}

      <Problems parses={parses} report={report} />

      {/* --------------------------------------------------------- two panes -- */}
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start">
        <div className="min-w-0 flex-1">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <div className="flex gap-1 rounded-lg bg-(--color-sunken) p-1">
              {(['steps', 'map', 'json'] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setView(value)}
                  className={[
                    'rounded-md px-3.5 py-1.5 text-body font-medium transition',
                    view === value ? 'bg-(--color-surface) text-(--color-ink) shadow-sm' : 'text-(--color-muted)',
                  ].join(' ')}
                >
                  {/* Not `capitalize`, which renders JSON as "Json". */}
                  {value === 'json' ? 'Definition' : value === 'map' ? 'Map' : 'Steps'}
                </button>
              ))}
            </div>

            {problems > 0 && (
              <span className="text-small text-(--color-muted)">
                {report?.errors.length ?? 0} to fix · {report?.warnings.length ?? 0} to look at
              </span>
            )}
          </div>

          {view === 'steps' && (
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2">
                  <span className="text-small font-medium text-(--color-muted)">Starts at</span>
                  <select
                    value={definition.start ?? ''}
                    onChange={(e) => write({ ...definition, start: e.target.value })}
                    className="rounded-lg border border-(--color-line) bg-(--color-paper) px-3 py-2 text-body"
                  >
                    <option value="">Choose…</option>
                    {Object.keys(nodes).map((id) => <option key={id} value={id}>{id}</option>)}
                  </select>
                </label>

                <label className="flex min-w-60 flex-1 items-center gap-2">
                  <span className="whitespace-nowrap text-small font-medium text-(--color-muted)">Wakes on</span>
                  <input
                    value={(definition.triggers ?? []).join(', ')}
                    onChange={(e) => write({
                      ...definition,
                      triggers: e.target.value.split(',').map((t) => t.trim()).filter(Boolean),
                    })}
                    placeholder="hi, hello, menu"
                    className="min-w-0 flex-1 rounded-lg border border-(--color-line) bg-(--color-paper) px-3 py-2 text-body"
                  />
                </label>

                <button
                  type="button"
                  onClick={addNode}
                  disabled={flow.isPublished}
                  className="ml-auto rounded-lg bg-(--color-brand) px-4 py-2 text-body font-medium text-white disabled:opacity-40"
                >
                  Add a step
                </button>
              </div>

              {Object.keys(nodes).length === 0 ? (
                <p className="rounded-lg border border-dashed border-(--color-line) px-4 py-10 text-center text-body text-(--color-muted)">
                  No steps yet.
                </p>
              ) : (
                Object.entries(nodes)
                  .sort(([a], [b]) => (a === selected ? -1 : b === selected ? 1 : 0))
                  .map(([id, node]) => (
                    <NodeEditor
                      key={id}
                      id={id}
                      node={node}
                      definition={definition}
                      actions={actions}
                      onChange={(patch) => patchNode(id, patch)}
                      onRename={(next) => renameNode(id, next)}
                      onRemove={() => removeNode(id)}
                    />
                  ))
              )}
            </div>
          )}

          {view === 'map' && (
            <FlowMap
              definition={definition}
              selected={selected}
              onSelect={(id) => { setSelected(id); setView('steps'); }}
            />
          )}

          {view === 'json' && (
            <JsonView
              text={text}
              parses={parses}
              readOnly={flow.isPublished}
              onChange={setText}
              onRevert={() => setText(flow.definition)}
              dirty={dirty}
            />
          )}
        </div>

        {/* The emulator is the other half of the screen from 1280 up, and a panel you
            toggle below that — a phone-width builder has no room for two panes and
            hiding the flow to look at the test would be worse than a button. */}
        <div className={[
          'w-full shrink-0 xl:w-112 xl:sticky xl:top-4',
          showTest ? '' : 'hidden xl:block',
        ].join(' ')}>
          <FlowEmulator
            definition={text}
            title={flow.name}
            rehearse={(body) => latest.current.rehearse(body)}
            disabled={!parses}
          />
        </div>
      </div>

      <p className="max-w-[90ch] text-small leading-relaxed text-(--color-muted)">
        Publishing freezes this version and opens the next one. Conversations already
        part-way through keep running the version they started on, so a change never
        lands on somebody mid-answer.
      </p>
    </div>
  );
}

/** The errors and warnings, told apart from "this is not JSON". */
function Problems({ parses, report }: { parses: boolean; report: FlowReport | null }) {
  if (!parses) {
    return (
      <p className="rounded-lg border border-(--color-bad) px-4 py-3 text-body text-(--color-bad)">
        The definition is not valid JSON, so nothing below has been checked. Fix it on
        the Definition tab — Format will tell you where.
      </p>
    );
  }

  if (!report || (report.errors.length === 0 && report.warnings.length === 0)) return null;

  return (
    <div className="flex flex-col gap-2">
      {report.errors.length > 0 && (
        <ul className="flex list-disc flex-col gap-1.5 rounded-lg border border-(--color-bad) py-3 pr-4 pl-8 text-body text-(--color-bad)">
          {report.errors.map((problem) => <li key={problem}>{problem}</li>)}
        </ul>
      )}
      {report.warnings.length > 0 && (
        <ul className="flex list-disc flex-col gap-1.5 rounded-lg border border-(--color-warn) bg-(--color-warn-wash) py-3 pr-4 pl-8 text-body text-(--color-ink-soft)">
          {report.warnings.map((warning) => <li key={warning}>{warning}</li>)}
        </ul>
      )}
    </div>
  );
}

/**
 * The definition itself, with the three things anybody editing JSON by hand needs.
 *
 * Format is not a nicety: a definition pasted from somewhere else arrives on one line,
 * and a parse error in a one-line document reports a column number nobody can act on.
 * Formatting it first is how you find the stray comma.
 */
function JsonView({ text, parses, readOnly, dirty, onChange, onRevert }: {
  text: string;
  parses: boolean;
  readOnly: boolean;
  dirty: boolean;
  onChange: (next: string) => void;
  onRevert: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const lines = text.split('\n').length;
  const size = new Blob([text]).size;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={!parses || readOnly}
          onClick={() => onChange(JSON.stringify(JSON.parse(text), null, 2))}
          className="rounded-lg border border-(--color-line) px-3.5 py-1.5 text-body disabled:opacity-40"
        >
          Format
        </button>
        <button
          type="button"
          onClick={() => { void navigator.clipboard?.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
          className="rounded-lg border border-(--color-line) px-3.5 py-1.5 text-body"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
        <button
          type="button"
          disabled={!dirty || readOnly}
          onClick={onRevert}
          className="rounded-lg border border-(--color-line) px-3.5 py-1.5 text-body disabled:opacity-40"
        >
          Discard changes
        </button>

        <span className="ml-auto font-mono text-small text-(--color-muted)">
          {lines} lines · {(size / 1024).toFixed(1)} KB · {parses ? 'valid JSON' : 'not valid JSON'}
        </span>
      </div>

      <textarea
        value={text}
        onChange={(e) => onChange(e.target.value)}
        readOnly={readOnly}
        spellCheck={false}
        wrap="off"
        rows={28}
        className={[
          'w-full rounded-lg border bg-(--color-paper) p-4 font-mono text-small leading-relaxed',
          parses ? 'border-(--color-line)' : 'border-(--color-bad)',
        ].join(' ')}
      />

      <p className="text-small text-(--color-muted)">
        The definition the interpreter runs. Checked as you type by the same rules that
        decide whether it can be published, and the test on the right runs whatever is
        in here — including changes you have not saved.
      </p>
    </div>
  );
}
